import { useState, type ReactNode } from "react";
import { clockTime, phaseLabel } from "../../lib/labels.js";
import { outputWindow, stepDetail, stepTone } from "../../lib/stepPresentation.js";
import { logGrowth, useStickToBottom } from "../../lib/useStickToBottom.js";
import type { Step, StepIo } from "../../lib/viewModel.js";

/**
 * The right pane: what the builder is doing, as a transcript.
 *
 * Chat-shaped, deliberately one-way. There is no composer and there must not
 * be one — the API exposes no route that accepts input into a running session,
 * and the read-only guarantee holds by the absence of surface on both sides. A
 * transcript that looked like it could be replied to would promise something
 * the system cannot do.
 *
 * Rows are steps, not events. The active step's timer advances in place, so a
 * quiet two-minute install reads as one thing taking a while rather than as
 * eight things repeating.
 *
 * Output belongs to the row that produced it, as an in/out block beneath the
 * step. It used to pool in a drawer at the foot of the pane, where "3,189
 * lines" was true and useless: no line could be traced back to the command that
 * printed it.
 *
 * The feed is framed rather than left to run into the edge of the window. Rows
 * used to end flush against the bottom of the pane, mid-sentence, with nothing
 * saying whether that was the end of the build or the end of the visible area —
 * so every state read as "there is something below this". The two pieces that
 * fix it are a terminus naming the real end of the feed and clear space beneath
 * it.
 *
 * Everything else here is one behaviour, and it lives in `lib/useStickToBottom`
 * because the hatch skin's log needs exactly the same of it: the feed follows
 * new activity until the user scrolls up, and re-locks the moment they scroll
 * back down. `following` is the single source for that, so the fade and the
 * jump control cannot appear while the view is locked — they are the "you have
 * scrolled away" state, not decoration.
 */
export function Transcript({ steps, isTerminal }: { steps: Step[]; isTerminal: boolean }) {
  const { scrollerRef, following, scrolled, jumpToLatest } = useStickToBottom(
    logGrowth(steps, isTerminal),
  );

  const lastIndex = steps.length - 1;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col border-l border-rule bg-surface">
      <div
        ref={scrollerRef}
        // The generous bottom padding is the point: the last row gets clear
        // space under it instead of stopping against the frame, which is what
        // made a finished feed look truncated.
        className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-12"
        role="log"
        aria-live="polite"
        aria-label="Build activity"
      >
        <ul className="space-y-1">
          {steps.map((step, i) => (
            <li key={step.id} className="animate-rise">
              <StepRow step={step} active={i === lastIndex && !isTerminal} />
            </li>
          ))}
        </ul>
        {steps.length > 0 && <Terminus isTerminal={isTerminal} />}
      </div>

      {/* Both fades sit over the scroller and are inert, so they dim content at
          the edges without intercepting a scroll or a click on a row. */}
      {scrolled && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-linear-to-b from-surface to-transparent"
        />
      )}
      {!following && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-surface via-surface/85 to-transparent"
          />
          <button
            type="button"
            onClick={jumpToLatest}
            className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-rule bg-surface px-3.5 py-1.5 text-xs text-ink-soft shadow-sm transition-colors hover:border-accent hover:text-accent"
          >
            Jump to latest
            <span aria-hidden className="leading-none">
              ↓
            </span>
          </button>
        </>
      )}
    </div>
  );
}

/**
 * The bottom of the feed, said out loud.
 *
 * Without it the last row is the last thing on screen and there is no way to
 * tell a finished build from a clipped one. Live it doubles as the "still
 * running" cue, which is why the wording differs rather than the marker just
 * disappearing when the build ends.
 */
function Terminus({ isTerminal }: { isTerminal: boolean }) {
  return (
    <p className="mt-3 flex items-center gap-3 px-3 text-[11px] uppercase tracking-[0.14em] text-ink-faint">
      {isTerminal ? (
        <>
          <span aria-hidden className="h-px flex-1 bg-rule" />
          End of build
          <span aria-hidden className="h-px flex-1 bg-rule" />
        </>
      ) : (
        <>
          <span aria-hidden className="size-1.5 rounded-full bg-accent animate-pulse-dot" />
          Working…
        </>
      )}
    </p>
  );
}

function StepRow({ step, active }: { step: Step; active: boolean }) {
  if (step.kind === "phase") {
    return (
      <p className="flex items-center gap-3 py-3 text-xs uppercase tracking-[0.14em] text-ink-faint">
        <span aria-hidden className="h-px flex-1 bg-rule" />
        {phaseLabel(step.label)}
        <span aria-hidden className="h-px flex-1 bg-rule" />
      </p>
    );
  }

  // An artifact step goes halt-red only when EVERY write in it failed — a
  // partial failure keeps the neutral dot and says "N failed" in its label,
  // since painting a mostly-successful burst red would overstate it. Same
  // colors as a failed terminal step; failure looks like failure everywhere.
  // Which of these a step is, is not this skin's decision — see
  // lib/stepPresentation. What the colours are, is.
  const tone = stepTone(step);
  const failed = tone === "failed";
  const succeeded = tone === "succeeded";

  return (
    <div
      className={[
        "flex gap-3 rounded-lg px-3 py-2.5 transition-colors",
        active ? "bg-accent-soft/50" : "",
      ].join(" ")}
    >
      <span className="flex w-3 shrink-0 justify-center pt-1.5" aria-hidden>
        <span
          className={[
            "size-1.5 rounded-full",
            failed
              ? "bg-halt"
              : succeeded
                ? "bg-done"
                : active
                  ? "bg-accent animate-pulse-dot"
                  : "bg-rule-strong",
          ].join(" ")}
        />
      </span>

      <div className="min-w-0 flex-1">
        <p
          className={[
            "text-sm leading-snug",
            failed ? "text-halt" : succeeded ? "text-done font-medium" : "text-ink",
          ].join(" ")}
        >
          {step.label}
        </p>

        <Detail step={step} active={active} />
        {step.io && <IoBlock io={step.io} />}
      </div>

      <span className="shrink-0 pt-0.5 text-[11px] tabular-nums text-ink-faint">
        {clockTime(step.at)}
      </span>
    </div>
  );
}

/**
 * A step's command and what it printed, as one block beneath the row.
 *
 * Both halves are labelled in a gutter rather than by punctuation, so a command
 * is distinguishable from its output at a glance even when both are shell text.
 */
function IoBlock({ io }: { io: StepIo }) {
  const [expanded, setExpanded] = useState(false);
  const { shown, hidden } = outputWindow(io, expanded);

  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-rule bg-sunk">
      {io.input !== undefined && (
        <Row label="in">
          <span className="text-ink">{io.input}</span>
        </Row>
      )}

      {io.output.length > 0 && (
        <Row label="out" divided={io.input !== undefined}>
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mb-1 block text-[11px] text-ink-faint transition-colors hover:text-accent"
            >
              {expanded ? "Show less" : `+${hidden} earlier ${hidden === 1 ? "line" : "lines"}`}
            </button>
          )}
          {/*
            Every line reads the same, whichever stream it came out of.
            `stderr` used to be painted in the failure colour, and on a healthy
            build that is simply wrong: npm writes its progress there, Vite
            writes its build report there, and the deploy prints "Expires in 0h
            58m" there. A feed with red in it says something went wrong, so a
            successful build looked like a broken one.

            What a step's outcome actually was is already said where it is
            known — the step's own dot and label — and that is the only place
            that can say it honestly.
          */}
          {shown.map((line) => (
            <div key={line.seq} className="text-ink-soft">
              {line.line}
            </div>
          ))}
        </Row>
      )}
    </div>
  );
}

function Row({
  label,
  divided,
  children,
}: {
  label: string;
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`flex gap-3 px-3 py-2 ${divided ? "border-t border-rule" : ""}`}>
      <span className="w-6 shrink-0 pt-px text-[10px] uppercase tracking-[0.12em] text-ink-faint">
        {label}
      </span>
      {/* `min-w-0` so an unbroken 300-character line wraps instead of widening
          the pane and forcing the whole transcript to scroll sideways. */}
      <div className="min-w-0 flex-1 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all">
        {children}
      </div>
    </div>
  );
}

/**
 * The secondary line: how many times, how long, and which files.
 *
 * This is where repetition goes. A count here is one row saying "this happened
 * four times"; without it the same sentence occupies four rows and the reader
 * cannot tell progress from a loop.
 */
function Detail({ step, active }: { step: Step; active: boolean }) {
  const detail = stepDetail(step, active);
  if (!detail) return null;
  const { parts, paths, hiddenPaths } = detail;

  return (
    <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs text-ink-faint">
      {parts.length > 0 && <span className="tabular-nums">{parts.join(" · ")}</span>}
      {paths.length > 0 && (
        <span className="min-w-0 truncate font-mono">
          {paths.join(", ")}
          {hiddenPaths > 0 && ` +${hiddenPaths}`}
        </span>
      )}
    </p>
  );
}
